import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Pagination,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable/dist/react-select-creatable.esm.js';
import debounce from 'lodash.debounce';
import PageTitle from '../../Components/PageTitle/PageTitle.js';
import CustomButton from '../../Components/Common/CustomButton/CustomButton';
import useDiscussionsClubsHostsInternalHosts from '../../hooks/useDiscussionsClubsHostsInternalHosts';
import { useGlobalContext } from '../../Context/index';
import {
    deleteMini,
    DiscussionOption,
    getAllDiscussions,
    getAllMinis,
    getAllTags,
    Mini,
    TagOption,
    UpsertMiniPayload,
    upsertMini,
} from './api';
import MinisBanners from './MinisBanners';

const PAGE_SIZE = 50;

const RESIZE_MODES = [
    { value: 'none', label: 'None' },
    { value: 'stretch', label: 'Stretch' },
    { value: 'cover', label: 'Cover' },
    { value: 'contain', label: 'Contain' },
];

const THUMBNAIL_WIDTH = 54;

const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const discussionUrl = (discussionid: string) =>
    `https://medflix.app/discussion/${discussionid}`;

type SelectOption = { value: string; label: string };

interface MiniFormState {
    id?: string;
    title: string;
    url: string;
    durationinms: string;
    thumbnailurl: string;
    transcript: string;
    resize_mode: string;
    discussion: SelectOption | null;
    tags: SelectOption[];
    creators: SelectOption[];
    isactive: boolean;
}

const emptyForm = (): MiniFormState => ({
    title: '',
    url: '',
    durationinms: '0',
    thumbnailurl: '',
    transcript: '',
    resize_mode: 'contain',
    discussion: null,
    tags: [],
    creators: [],
    isactive: true,
});

const formatDuration = (ms: number) => {
    if (!ms) return '—';
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
};

const discussionToOption = (d: DiscussionOption): SelectOption | null => {
    if (!d?.discussionid) return null;
    const label = d.name
        ? d.startdate
            ? `${d.name} | ${new Date(d.startdate).toLocaleString()}`
            : d.name
        : d.discussionid;
    return { value: d.discussionid, label };
};

const mergeDiscussionOptions = (
    selected: SelectOption | null,
    options: SelectOption[],
): SelectOption[] => {
    if (!selected) return options;
    if (options.some(o => o.value === selected.value)) return options;
    return [selected, ...options];
};

const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: { menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }) },
};

const miniToForm = (mini: Mini): MiniFormState => ({
    id: mini.id,
    title: mini.title,
    url: mini.url,
    durationinms: String(mini.durationinms ?? 0),
    thumbnailurl: mini.thumbnailurl ?? '',
    transcript: mini.transcript ?? '',
    resize_mode: mini.resizeMode ?? 'contain',
    discussion: mini.discussion
        ? { value: mini.discussion.discussionid, label: mini.discussion.name }
        : null,
    tags: (mini.tags ?? []).map(tag => ({ value: tag.id, label: tag.name })),
    creators: (mini.creators ?? []).map(creator => ({
        value: creator.accountid,
        label: `${creator.firstname} ${creator.lastname}`.trim(),
    })),
    isactive: Boolean(mini.isactive),
});

const miniToUpsertPayload = (
    mini: Mini,
    overrides: Partial<UpsertMiniPayload> = {},
): UpsertMiniPayload => ({
    id: mini.id,
    title: mini.title,
    url: mini.url,
    durationinms: mini.durationinms ?? 0,
    thumbnailurl: mini.thumbnailurl || undefined,
    transcript: mini.transcript || undefined,
    resize_mode: mini.resizeMode ?? 'contain',
    discussionid: mini.discussion?.discussionid ?? null,
    creators: (mini.creators ?? []).map(c => ({ accountid: c.accountid })),
    tags: (mini.tags ?? []).map(t => ({ id: t.id })),
    isactive: 1,
    ...overrides,
});

const detectVideoDuration = (url: string): Promise<number> =>
    new Promise(resolve => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src);
            resolve(Math.round(video.duration * 1000));
        };
        video.onerror = () => resolve(0);
        video.src = url;
    });

const Minis: React.FC = () => {
    const { setShowSuccessToast, setShowErrorToast } = useGlobalContext();
    const { selectSpeakers } = useDiscussionsClubsHostsInternalHosts({
        getSelectSpeakers: true,
    });

    const [minis, setMinis] = useState<Mini[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');

    const [showFormModal, setShowFormModal] = useState(false);
    const [form, setForm] = useState<MiniFormState>(emptyForm());
    const [formError, setFormError] = useState('');
    const [detectingDuration, setDetectingDuration] = useState(false);

    const [miniToDelete, setMiniToDelete] = useState<Mini | null>(null);

    const [tagOptions, setTagOptions] = useState<SelectOption[]>([]);
    const [discussionOptions, setDiscussionOptions] = useState<SelectOption[]>([]);

    const fetchMinis = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getAllMinis({
                count: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
                searchquery: searchQuery,
                includeInactive,
            });
            setMinis(data);
            setHasMore(data.length === PAGE_SIZE);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load minis';
            setError(message);
            setMinis([]);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [page, searchQuery, includeInactive]);

    useEffect(() => {
        fetchMinis();
    }, [fetchMinis]);

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value: string) => {
                setSearchQuery(value.trim());
                setPage(1);
            }, 400),
        [],
    );

    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    const loadTags = useCallback(async (search?: string) => {
        try {
            const tags = await getAllTags(search);
            if (!Array.isArray(tags)) {
                setTagOptions([]);
                return;
            }
            setTagOptions(
                tags
                    .filter((tag: TagOption) => tag?.id && tag?.name)
                    .map((tag: TagOption) => ({ value: tag.id, label: tag.name })),
            );
        } catch {
            // non-blocking for form
        }
    }, []);

    const loadDiscussions = useCallback(async (search?: string) => {
        try {
            const discussions = await getAllDiscussions({
                count: 50,
                searchquery: search,
            });
            if (!Array.isArray(discussions)) {
                setDiscussionOptions([]);
                return;
            }
            setDiscussionOptions(
                discussions
                    .map((d: DiscussionOption) => discussionToOption(d))
                    .filter((o): o is SelectOption => o !== null),
            );
        } catch {
            // keep existing options on search failure
        }
    }, []);

    const debouncedLoadDiscussions = useMemo(
        () => debounce((value: string) => loadDiscussions(value), 300),
        [loadDiscussions],
    );

    useEffect(() => () => debouncedLoadDiscussions.cancel(), [debouncedLoadDiscussions]);

    const handleDiscussionInputChange = (
        value: string,
        actionMeta: { action: string },
    ) => {
        if (actionMeta.action !== 'input-change') return;
        debouncedLoadDiscussions(value);
    };

    const handleTagInputChange = (value: string, actionMeta: { action: string }) => {
        if (actionMeta.action !== 'input-change') return;
        loadTags(value);
    };

    const openCreateModal = () => {
        setForm(emptyForm());
        setFormError('');
        setShowFormModal(true);
        loadTags();
        loadDiscussions();
    };

    const openEditModal = (mini: Mini) => {
        setForm(miniToForm(mini));
        setFormError('');
        setShowFormModal(true);
        loadTags();
        loadDiscussions(mini.discussion?.name);
    };

    const handleUrlBlur = async () => {
        if (!form.url.trim() || form.durationinms !== '0') return;
        setDetectingDuration(true);
        const ms = await detectVideoDuration(form.url.trim());
        if (ms > 0) {
            setForm(prev => ({ ...prev, durationinms: String(ms) }));
        }
        setDetectingDuration(false);
    };

    const handleSave = async () => {
        const title = form.title.trim();
        const url = form.url.trim();
        if (!title) {
            setFormError('Title is required.');
            return;
        }
        if (!url) {
            setFormError('Video URL is required.');
            return;
        }

        setActionLoading(true);
        setFormError('');
        try {
            const existingTagIds = new Set(tagOptions.map(t => t.value));
            const tags = form.tags.map(tag =>
                existingTagIds.has(tag.value)
                    ? { id: tag.value }
                    : { name: tag.label },
            );

            await upsertMini({
                ...(form.id ? { id: form.id } : {}),
                title,
                url,
                durationinms: Number(form.durationinms) || 0,
                thumbnailurl: form.thumbnailurl.trim() || undefined,
                transcript: form.transcript.trim() || undefined,
                resize_mode: form.resize_mode,
                discussionid: form.discussion?.value ?? null,
                creators: form.creators.map(c => ({ accountid: c.value })),
                tags,
                ...(form.id ? { isactive: form.isactive ? 1 : 0 } : {}),
            });
            setShowFormModal(false);
            setShowSuccessToast(true);
            await fetchMinis();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to save mini';
            setFormError(message);
            setShowErrorToast(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRestore = async (mini: Mini) => {
        setActionLoading(true);
        setError('');
        try {
            await upsertMini(miniToUpsertPayload(mini, { isactive: 1 }));
            setShowSuccessToast(true);
            await fetchMinis();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to restore mini';
            setError(message);
            setShowErrorToast(true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!miniToDelete) return;
        setActionLoading(true);
        try {
            await deleteMini(miniToDelete.id);
            setMiniToDelete(null);
            setShowSuccessToast(true);
            if (minis.length === 1 && page > 1) {
                setPage(prev => prev - 1);
            } else {
                await fetchMinis();
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete mini';
            setError(message);
            setShowErrorToast(true);
            setMiniToDelete(null);
        } finally {
            setActionLoading(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        if (!value.trim()) {
            debouncedSetSearch.cancel();
            setSearchQuery('');
            setPage(1);
        } else {
            debouncedSetSearch(value);
        }
    };

    return (
        <Container fluid style={{ padding: '20px' }}>
            <Row className="align-items-center mb-3">
                <Col>
                    <PageTitle>Minis</PageTitle>
                </Col>
                <Col className="text-end">
                    <CustomButton onClick={openCreateModal}>+ Mini</CustomButton>
                </Col>
            </Row>

            <MinisBanners />

            <Row className="align-items-center g-2 mb-3">
                <Col md={4}>
                    <Form.Control
                        type="search"
                        placeholder="Search title or description..."
                        value={searchInput}
                        onChange={handleSearchChange}
                    />
                </Col>
                <Col md="auto">
                    <Form.Check
                        type="switch"
                        id="include-inactive"
                        label="Include inactive"
                        checked={includeInactive}
                        onChange={e => {
                            setIncludeInactive(e.target.checked);
                            setPage(1);
                        }}
                    />
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Thumbnail</th>
                                <th>Title</th>
                                <th>Duration</th>
                                <th>Resize</th>
                                <th>Tags</th>
                                <th>Creators</th>
                                <th>Discussion</th>
                                <th>Added on</th>
                                <th>Status</th>
                                <th style={{ width: '180px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {minis.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="text-center text-muted">
                                        No minis found
                                    </td>
                                </tr>
                            ) : (
                                minis.map(mini => (
                                    <tr key={mini.id} style={{ opacity: mini.isactive ? 1 : 0.6 }}>
                                        <td>
                                            {mini.thumbnailurl ? (
                                                <img
                                                    src={mini.thumbnailurl}
                                                    alt={mini.title}
                                                    style={{
                                                        width: THUMBNAIL_WIDTH,
                                                        aspectRatio: '9 / 16',
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        display: 'block',
                                                    }}
                                                />
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 500 }}>{mini.title}</div>
                                            <a
                                                href={mini.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ fontSize: 12 }}
                                            >
                                                Video
                                            </a>
                                            {mini.transcript && (
                                                <>
                                                    {' · '}
                                                    <a
                                                        href={mini.transcript}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ fontSize: 12 }}
                                                    >
                                                        Transcript
                                                    </a>
                                                </>
                                            )}
                                        </td>
                                        <td>{formatDuration(mini.durationinms)}</td>
                                        <td>{mini.resizeMode ?? 'contain'}</td>
                                        <td>
                                            {(mini.tags ?? []).map(tag => (
                                                <Badge
                                                    key={tag.id}
                                                    bg="secondary"
                                                    className="me-1 mb-1"
                                                >
                                                    {tag.name}
                                                </Badge>
                                            ))}
                                        </td>
                                        <td style={{ fontSize: 13 }}>
                                            {(mini.creators ?? [])
                                                .map(c =>
                                                    `${c.firstname} ${c.lastname}`.trim(),
                                                )
                                                .join(', ') || '—'}
                                        </td>
                                        <td style={{ fontSize: 13 }}>
                                            {mini.discussion ? (
                                                <a
                                                    href={discussionUrl(
                                                        mini.discussion.discussionid,
                                                    )}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {mini.discussion.name}
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                            {formatDate(mini.createdon)}
                                        </td>
                                        <td>
                                            <Badge bg={mini.isactive ? 'success' : 'secondary'}>
                                                {mini.isactive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-1 mb-1"
                                                onClick={() => openEditModal(mini)}
                                                disabled={actionLoading}
                                            >
                                                Edit
                                            </Button>
                                            {mini.isactive ? (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="mb-1"
                                                    onClick={() => setMiniToDelete(mini)}
                                                    disabled={actionLoading}
                                                >
                                                    Delete
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    className="mb-1"
                                                    onClick={() => handleRestore(mini)}
                                                    disabled={actionLoading}
                                                >
                                                    Restore
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>

                    <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ paddingBottom: 150 }}
                    >
                        <span className="text-muted">
                            Page {page}
                            {minis.length > 0 &&
                                ` · showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + minis.length}`}
                        </span>
                        <Pagination className="mb-0">
                            <Pagination.Prev
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                            />
                            <Pagination.Item active>{page}</Pagination.Item>
                            <Pagination.Next
                                disabled={!hasMore}
                                onClick={() => setPage(p => p + 1)}
                            />
                        </Pagination>
                    </div>
                </>
            )}

            <Modal
                show={showFormModal}
                onHide={() => !actionLoading && setShowFormModal(false)}
                size="lg"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{form.id ? 'Edit Mini' : 'Add Mini'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    <Form>
                        {form.id && (
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="mini-active"
                                    label="Active"
                                    checked={form.isactive}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            isactive: e.target.checked,
                                        }))
                                    }
                                />
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control
                                value={form.title}
                                onChange={e =>
                                    setForm(prev => ({ ...prev, title: e.target.value }))
                                }
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Video URL *</Form.Label>
                            <Form.Control
                                value={form.url}
                                onChange={e =>
                                    setForm(prev => ({ ...prev, url: e.target.value }))
                                }
                                onBlur={handleUrlBlur}
                                required
                            />
                            {detectingDuration && (
                                <Form.Text className="text-muted">
                                    Detecting duration…
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Duration (ms)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        value={form.durationinms}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                durationinms: e.target.value,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Resize Mode</Form.Label>
                                    <Form.Select
                                        value={form.resize_mode}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                resize_mode: e.target.value,
                                            }))
                                        }
                                    >
                                        {RESIZE_MODES.map(mode => (
                                            <option key={mode.value} value={mode.value}>
                                                {mode.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Thumbnail URL</Form.Label>
                            <Form.Control
                                value={form.thumbnailurl}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        thumbnailurl: e.target.value,
                                    }))
                                }
                            />
                            {form.thumbnailurl && (
                                <img
                                    src={form.thumbnailurl}
                                    alt=""
                                    style={{
                                        marginTop: 8,
                                        width: 72,
                                        aspectRatio: '9 / 16',
                                        objectFit: 'cover',
                                        borderRadius: 4,
                                    }}
                                />
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Transcript URL</Form.Label>
                            <Form.Control
                                type="url"
                                placeholder="https://..."
                                value={form.transcript}
                                onChange={e =>
                                    setForm(prev => ({
                                        ...prev,
                                        transcript: e.target.value,
                                    }))
                                }
                            />
                            {form.transcript && (
                                <Form.Text>
                                    <a href={form.transcript} target="_blank" rel="noreferrer">
                                        Open transcript
                                    </a>
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Discussion</Form.Label>
                            <Select
                                isClearable
                                isSearchable
                                placeholder="Search discussions..."
                                options={mergeDiscussionOptions(
                                    form.discussion,
                                    discussionOptions,
                                )}
                                value={form.discussion}
                                onChange={val =>
                                    setForm(prev => ({
                                        ...prev,
                                        discussion: val as SelectOption | null,
                                    }))
                                }
                                onInputChange={handleDiscussionInputChange}
                                filterOption={() => true}
                                {...selectMenuPortalProps}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Tags</Form.Label>
                            <CreatableSelect
                                isMulti
                                placeholder="Select or create tags..."
                                options={tagOptions}
                                value={form.tags}
                                onChange={val =>
                                    setForm(prev => ({
                                        ...prev,
                                        tags: (val as SelectOption[]) ?? [],
                                    }))
                                }
                                onInputChange={handleTagInputChange}
                                onCreateOption={inputValue => {
                                    const newTag = {
                                        value: `new:${inputValue}`,
                                        label: inputValue,
                                    };
                                    setForm(prev => ({
                                        ...prev,
                                        tags: [...prev.tags, newTag],
                                    }));
                                }}
                                {...selectMenuPortalProps}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Creators</Form.Label>
                            <Select
                                isMulti
                                isSearchable
                                placeholder="Select creators..."
                                options={selectSpeakers ?? []}
                                value={form.creators}
                                onChange={val =>
                                    setForm(prev => ({
                                        ...prev,
                                        creators: (val as SelectOption[]) ?? [],
                                    }))
                                }
                                {...selectMenuPortalProps}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowFormModal(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={actionLoading}>
                        {actionLoading ? (
                            <Spinner size="sm" animation="border" />
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={!!miniToDelete}
                onHide={() => !actionLoading && setMiniToDelete(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Mini</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Soft-delete <strong>{miniToDelete?.title}</strong>? It will be marked
                    inactive.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setMiniToDelete(null)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
                        {actionLoading ? (
                            <Spinner size="sm" animation="border" />
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default Minis;
